import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import TemplateCard from './TemplateCard';
import TemplateEditor from './TemplateEditor';
import TemplateStats from './TemplateStats';


const RecurringTemplatesList = ({ context, contextId, contextName }) => {
  // context: 'client' | 'team' | 'personal'
  // contextId: client_id, team_id, or user_id
  // contextName: Display name for context
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [filter, setFilter] = useState('active');
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchTemplates();
  }, [context, contextId, filter]);
  
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('access_token');
      const params = {};
      
      if (context === 'client') {
        params.client_id = contextId;
      } else if (context === 'team') {
        params.team_id = contextId;
        params.type = 'team';
      } else if (context === 'personal') {
        params.personal = true;
      }
      
      const response = await api.get(`/recurring-templates`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      setError(error.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreate = () => {
    setEditingTemplate(null);
    setShowEditor(true);
  };
  
  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };
  
  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Delete recurring template "${templateName}"?\n\nGenerated tasks will remain, but no new tasks will be created.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/recurring-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTemplates();
      showToast('Template deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete template:', error);
      showToast('Failed to delete template', 'error');
    }
  };
  
  const handlePause = async (templateId) => {
    try {
      const token = localStorage.getItem('access_token');
      await api.post(`/recurring-templates/${templateId}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTemplates();
      showToast('Template paused', 'success');
    } catch (error) {
      console.error('Failed to pause template:', error);
      showToast('Failed to pause template', 'error');
    }
  };
  
  const handleResume = async (templateId) => {
    try {
      const token = localStorage.getItem('access_token');
      await api.post(`/recurring-templates/${templateId}/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTemplates();
      showToast('Template resumed', 'success');
    } catch (error) {
      console.error('Failed to resume template:', error);
      showToast('Failed to resume template', 'error');
    }
  };
  
  const showToast = (message, type = 'info') => {
    // Simple toast implementation - can be replaced with your toast library
    alert(message);
  };
  
  const filteredTemplates = templates.filter(t => {
    if (filter === 'active') return !t.is_paused;
    if (filter === 'paused') return t.is_paused;
    return true;
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <p className="font-semibold">Error loading templates</p>
        <p className="text-sm">{error}</p>
        <button 
          onClick={fetchTemplates}
          className="mt-2 text-sm underline"
        >
          Try again
        </button>
      </div>
    );
  }
  
  return (
    <div className="recurring-templates-container">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-3xl">🔄</span>
            Recurring Templates
            {contextName && (
              <span className="text-lg font-normal text-gray-500">
                for {contextName}
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Automatically generate tasks on a recurring schedule
          </p>
        </div>
        
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
        >
          <span className="text-xl">+</span>
          Create Template
        </button>
      </div>
      
      {/* Stats Summary */}
      {templates.length > 0 && <TemplateStats templates={templates} />}
      
      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilter('active')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filter === 'active' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Active ({templates.filter(t => !t.is_paused).length})
        </button>
        <button
          onClick={() => setFilter('paused')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filter === 'paused' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Paused ({templates.filter(t => t.is_paused).length})
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All ({templates.length})
        </button>
      </div>
      
      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-dashed border-blue-200">
          <div className="text-7xl mb-4">🔄</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {templates.length === 0 ? 'No recurring templates yet' : 'No templates match this filter'}
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {templates.length === 0 
              ? 'Create a template to automatically generate tasks on a recurring schedule'
              : 'Try adjusting your filter to see more templates'
            }
          </p>
          {templates.length === 0 && (
            <button
              onClick={handleCreate}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 inline-flex items-center gap-2 transition-colors"
            >
              <span className="text-xl">+</span>
              Create Your First Template
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => handleEdit(template)}
              onDelete={() => handleDelete(template.id, template.name)}
              onPause={() => handlePause(template.id)}
              onResume={() => handleResume(template.id)}
            />
          ))}
        </div>
      )}
      
      {/* Editor Modal */}
      {showEditor && (
        <TemplateEditor
          template={editingTemplate}
          context={context}
          contextId={contextId}
          onClose={() => {
            setShowEditor(false);
            setEditingTemplate(null);
          }}
          onSave={() => {
            setShowEditor(false);
            setEditingTemplate(null);
            fetchTemplates();
          }}
        />
      )}
    </div>
  );
};

export default RecurringTemplatesList;
